
import {
  computed,
  defineComponent, PropType, ref, Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';

import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import {
  FieldConfig, ParserFieldObject, Property, SpacesFieldOption,
} from '@/types';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';

export default defineComponent({
  name: 'Spaces',
  components: {
    ToggleDisplaySettings,
    Button,
    BaseInputNumber,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const configs = ref(props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configs.value, 'displayToggle') || configs.value.displayToggle === true));
    const SIGN = ref('px');
    const baseProperty: Ref<string> = ref(props.configs.options.property);
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties);

    const buildInputList = (): SpacesFieldOption[] => Object.entries(props.configs.options).filter((data) => data[0] !== 'property')
      .map((data) => {
        const currentPropertyName = baseProperty.value === 'border-radius' ? `border-${data[0]}-radius` : `${baseProperty.value}-${data[0]}`;
        const parserValue = props.parserValues.properties.filter((property) => property.name === currentPropertyName)[0].value?.replace(new RegExp(SIGN.value, 'g'), '');
        const { defaultValue, title } = data[1];
        const value = !Number.isNaN(Number(parserValue)) ? Number(parserValue) : Number(defaultValue);
        return {
          title: t(title),
          property: currentPropertyName,
          defaultValue,
          value,
        };
      });
    const inputList: Ref<SpacesFieldOption[]> = ref(buildInputList());

    const checkIfAllValuesEqual = (): boolean => {
      if (inputList.value.length <= 1) return true;
      const firstValue = inputList.value[0].value;
      return inputList.value.every((item) => item.value === firstValue);
    };

    const allValuesEqual = checkIfAllValuesEqual();
    const advancedMode = ref(!allValuesEqual);
    const allLocked = ref(allValuesEqual);

    // Helper to find input by position name (top, bottom, left, right)
    // For border-radius: visual layout is top-left (top), top-right (right), bottom-right (bottom), bottom-left (left)
    const findInputByPosition = (position: string) => {
      let index = -1;

      if (baseProperty.value === 'border-radius') {
        // Border-radius uses corner names: top-left, top-right, bottom-left, bottom-right
        // Map visual positions to corner names
        const cornerMap: Record<string, string> = {
          top: 'top-left',
          right: 'top-right',
          bottom: 'bottom-right',
          left: 'bottom-left',
        };
        const corner = cornerMap[position];
        if (corner) {
          index = inputList.value.findIndex((item) => item.property.includes(`-${corner}-`));
        }
      } else {
        // Standard margin/padding: property ends with -top, -right, -bottom, -left
        index = inputList.value.findIndex((item) => item.property.endsWith(`-${position}`) || item.property.includes(`-${position}-`));
      }

      if (index !== -1) {
        return { ...inputList.value[index], index };
      }
      return { value: 0, property: '', defaultValue: 0, title: '', index: -1 };
    };

    const topInput = computed(() => findInputByPosition('top'));
    const bottomInput = computed(() => findInputByPosition('bottom'));
    const leftInput = computed(() => findInputByPosition('left'));
    const rightInput = computed(() => findInputByPosition('right'));

    const isBorderRadius = computed(() => baseProperty.value === 'border-radius');

    const unifiedValue = computed({
      get: () => inputList.value[0]?.value ?? 0,
      set: (val: number) => {
        inputList.value.forEach((item) => {
          item.value = val;
        });
      },
    });

    const toggleAdvancedMode = () => {
      advancedMode.value = !advancedMode.value;
      if (advancedMode.value) {
        allLocked.value = checkIfAllValuesEqual();
      }
    };

    const toggleAllLocked = () => {
      allLocked.value = !allLocked.value;
      if (allLocked.value) {
        const firstValue = inputList.value[0].value;
        inputList.value.forEach((item) => {
          item.value = firstValue;
        });
        const updatedProperties = properties.value.map((prop: Property) => {
          const updatedProperty = prop;
          updatedProperty.value = `${firstValue}${SIGN.value}`;
          return updatedProperty;
        });
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: updatedProperties,
        });
      }
    };

    const handleUnifiedInput = () => {
      const newValue = unifiedValue.value;
      inputList.value.forEach((item) => {
        item.value = newValue;
      });
      const updatedProperties = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = `${newValue}${SIGN.value}`;
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    const handleInput = (input: SpacesFieldOption, index: number) => {
      const { value, property, defaultValue } = input;
      const newValue: number = value === null ? Number(defaultValue) : value;

      if (allLocked.value) {
        inputList.value.forEach((item) => {
          item.value = newValue;
        });
        const updatedProperties = properties.value.map((prop: Property) => {
          const updatedProperty = prop;
          updatedProperty.value = `${newValue}${SIGN.value}`;
          return updatedProperty;
        });
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: updatedProperties,
        });
      } else {
        const updatedProperty: Property = properties.value.filter((prop: Property) => prop.name === property)[0];
        updatedProperty.value = `${newValue}${SIGN.value}`;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [updatedProperty],
        });
        // Don't auto-lock when values become equal - let user control the lock manually
      }
    };

    return {
      t,
      inputList,
      SIGN,
      displayProperties,
      handleInput,
      advancedMode,
      allLocked,
      unifiedValue,
      toggleAdvancedMode,
      toggleAllLocked,
      handleUnifiedInput,
      topInput,
      bottomInput,
      leftInput,
      rightInput,
      isBorderRadius,
    };
  },
});
